import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  Card,
  Select,
  Button,
  Table,
  Modal,
  Space,
  Typography,
  Alert,
  Tag,
  Timeline,
} from "antd";
import {
  ExclamationCircleOutlined,
  SwapOutlined,
  RollbackOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import Breadcrumb from "../../components/Breadcrumb";
import { useToast } from "../../components/Toast/toastHook";
import { GET_COMPANIES_LISTING } from "../../graphql/companyQueries";
import {
  GET_MERGES_BY_STATUS,
  GET_MERGE_DETAILS,
  INITIATE_MERGE,
  EXECUTE_MERGE,
  ROLLBACK_MERGE,
} from "../../graphql/mergeQueries";
import { TABLE_SCROLL } from "../../constants/table";

const { Option } = Select;
const { Title, Text } = Typography;
const { confirm } = Modal;

const CompanyMerge = () => {
  const [sourceCompanyId, setSourceCompanyId] = useState(null);
  const [targetCompanyId, setTargetCompanyId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("pending");
  const [mergeDetailsVisible, setMergeDetailsVisible] = useState(false);
  const [selectedMergeId, setSelectedMergeId] = useState(null);

  const toast = useToast(5000);

  // Query companies for dropdown
  const { data: companiesData, loading: companiesLoading } = useQuery(
    GET_COMPANIES_LISTING,
    {
      fetchPolicy: "cache-and-network",
    }
  );

  // Query merges by status
  const {
    data: mergesData,
    loading: mergesLoading,
    refetch: refetchMerges,
  } = useQuery(GET_MERGES_BY_STATUS, {
    variables: { status: selectedStatus },
    fetchPolicy: "cache-and-network",
  });

  // Query merge details
  const { data: mergeDetailsData, loading: detailsLoading } = useQuery(
    GET_MERGE_DETAILS,
    {
      variables: { mergeId: selectedMergeId },
      skip: !selectedMergeId,
      fetchPolicy: "cache-and-network",
    }
  );

  // Mutations
  const [initiateMerge, { loading: initiateLoading }] = useMutation(
    INITIATE_MERGE,
    {
      onCompleted: (data) => {
        toast(
          "success",
          `Merge initiated successfully! Merge ID: ${data.initiateMerge._id}`
        );
        setSourceCompanyId(null);
        setTargetCompanyId(null);
        refetchMerges();
      },
      onError: (error) => {
        toast("error", `Failed to initiate merge: ${error.message}`);
      },
    }
  );

  const [executeMerge, { loading: executeLoading }] = useMutation(
    EXECUTE_MERGE,
    {
      onCompleted: (data) => {
        toast(
          "success",
          `Merge executed successfully! Duration: ${data.executeMerge.duration}ms`
        );
        refetchMerges();
      },
      onError: (error) => {
        toast("error", `Failed to execute merge: ${error.message}`);
      },
    }
  );

  const [rollbackMerge, { loading: rollbackLoading }] = useMutation(
    ROLLBACK_MERGE,
    {
      onCompleted: (data) => {
        toast("success", `Merge rolled back successfully!`);
        refetchMerges();
      },
      onError: (error) => {
        toast("error", `Failed to rollback merge: ${error.message}`);
      },
    }
  );

  const handleInitiateMerge = () => {
    if (!sourceCompanyId || !targetCompanyId) {
      toast("error", "Please select both source and target companies");
      return;
    }

    if (sourceCompanyId === targetCompanyId) {
      toast("error", "Source and target companies cannot be the same");
      return;
    }

    const sourceCompany = companiesData?.getAllCompanyList?.find(
      (c) => c._id === sourceCompanyId
    );
    const targetCompany = companiesData?.getAllCompanyList?.find(
      (c) => c._id === targetCompanyId
    );

    confirm({
      title: "Confirm Merge Initiation",
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            <strong>Source Company:</strong> {sourceCompany?.companyName}
          </p>
          <p>
            <strong>Target Company:</strong> {targetCompany?.companyName}
          </p>
          <p style={{ color: "red", marginTop: 16 }}>
            <strong>Warning:</strong> This will prepare to merge all data from
            the source company into the target company. The source company will
            become inactive after execution.
          </p>
        </div>
      ),
      okText: "Initiate",
      cancelText: "Cancel",
      okButtonProps: {
        style: {
          backgroundColor: "#b4c540",
          borderColor: "#b4c540",
          color: "#fff",
        },
      },
      cancelButtonProps: {
        style: {
          backgroundColor: "#6c757d",
          borderColor: "#6c757d",
          color: "#fff",
        },
      },
      onOk() {
        initiateMerge({
          variables: {
            input: {
              sourceCompanyId,
              targetCompanyId,
            },
          },
        });
      },
    });
  };

  const handleExecuteMerge = (mergeId, sourceName, targetName) => {
    confirm({
      title: "Execute Merge",
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to execute this merge?</p>
          <p>
            <strong>Source:</strong> {sourceName} → <strong>Target:</strong>{" "}
            {targetName}
          </p>
          <p style={{ color: "red", marginTop: 16 }}>
            <strong>Warning:</strong> This action cannot be undone easily. All
            data will be transferred.
          </p>
        </div>
      ),
      okText: "Execute",
      cancelText: "Cancel",
      okButtonProps: {
        style: {
          backgroundColor: "#b4c540",
          borderColor: "#b4c540",
          color: "#fff",
        },
      },
      cancelButtonProps: {
        style: {
          backgroundColor: "#6c757d",
          borderColor: "#6c757d",
          color: "#fff",
        },
      },
      onOk() {
        executeMerge({ variables: { mergeId } });
      },
    });
  };

  const handleRollbackMerge = (mergeId, sourceName, targetName) => {
    Modal.confirm({
      title: "Rollback Merge",
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to rollback this merge?</p>
          <p>
            <strong>Source:</strong> {sourceName} ← <strong>Target:</strong>{" "}
            {targetName}
          </p>
          <p style={{ color: "orange", marginTop: 16 }}>
            This will restore all data to its original state before the merge.
          </p>
          <input
            id="rollbackReason"
            placeholder="Enter rollback reason..."
            style={{ width: "100%", marginTop: 8, padding: 8 }}
          />
        </div>
      ),
      okText: "Rollback",
      cancelText: "Cancel",
      okButtonProps: {
        style: {
          backgroundColor: "#b4c540",
          borderColor: "#b4c540",
          color: "#fff",
        },
      },
      cancelButtonProps: {
        style: {
          backgroundColor: "#6c757d",
          borderColor: "#6c757d",
          color: "#fff",
        },
      },
      onOk() {
        const reason =
          document.getElementById("rollbackReason")?.value ||
          "No reason provided";
        rollbackMerge({
          variables: {
            input: { mergeId, reason },
          },
        });
      },
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "orange",
      in_progress: "blue",
      completed: "green",
      failed: "red",
      rolled_back: "purple",
    };
    return colors[status] || "default";
  };

  const mergeColumns = [
    {
      title: "Source Company",
      dataIndex: ["sourceCompany", "companyName"],
      key: "sourceCompany",
      className: "font-medium",
    },
    {
      title: "Target Company",
      dataIndex: ["targetCompany", "companyName"],
      key: "targetCompany",
      className: "font-medium",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
      align: "center",
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => {
        if (!date) return "-";
        try {
          const dateObj = new Date(Number(date));
          if (isNaN(dateObj.getTime())) return "Invalid Date";
          return dateObj.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        } catch (error) {
          return "Invalid Date";
        }
      },
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<EyeOutlined />}
            size="middle"
            shape="circle"
            className="border-indigo-500 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-600 w-8 h-8 flex items-center justify-center"
            title="View Details"
            onClick={() => {
              setSelectedMergeId(record._id);
              setMergeDetailsVisible(true);
            }}
          />
          {record.status === "pending" && (
            <Button
              icon={<SwapOutlined />}
              size="middle"
              shape="circle"
              className="bg-emerald-500 text-white border-0 hover:bg-emerald-600 w-8 h-8 flex items-center justify-center"
              title="Execute Merge"
              loading={executeLoading}
              onClick={() =>
                handleExecuteMerge(
                  record._id,
                  record.sourceCompany.companyName,
                  record.targetCompany.companyName
                )
              }
            />
          )}
          {record.status === "completed" && (
            <Button
              icon={<RollbackOutlined />}
              size="middle"
              shape="circle"
              className="bg-amber-500 text-white border-0 hover:bg-amber-600 w-8 h-8 flex items-center justify-center"
              title="Rollback Merge"
              loading={rollbackLoading}
              onClick={() =>
                handleRollbackMerge(
                  record._id,
                  record.sourceCompany.companyName,
                  record.targetCompany.companyName
                )
              }
            />
          )}
        </Space>
      ),
    },
  ];

  const companies = companiesData?.getAllCompanyList || [];

  return (
    <div>
      <Breadcrumb
        pageName="Company Merge"
        items={[
          { label: "Dashboard", link: "/administrator/dashboard" },
          { label: "Company Merge" },
        ]}
      />

      {/* Initiate Merge Section */}
      <Card
        className="rounded-sm border border-stroke bg-white shadow-lg mb-6 p-3"
        title={
          <div className="border-b border-stroke py-4 px-6.5">
            <h3 className="font-medium text-black">Initiate Company Merge</h3>
          </div>
        }
        headStyle={{ padding: 0, border: "none" }}
        bodyStyle={{ padding: "25px" }}
      >
        <div className="mb-6">
          <Alert
            message="Company Merge Process"
            description="This is a two-step process: First initiate the merge to analyze what will be transferred, then execute to perform the actual merge."
            type="info"
            showIcon
            className="mb-4"
          />
        </div>

        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-60">
            <label className="mb-2.5 block text-black font-medium">
              Source Company (will be merged):
            </label>
            <Select
              className="w-full"
              placeholder="Select source company"
              value={sourceCompanyId}
              onChange={setSourceCompanyId}
              loading={companiesLoading}
              size="large"
            >
              {companies.map((company) => (
                <Option key={company._id} value={company._id}>
                  {company.companyName}
                </Option>
              ))}
            </Select>
          </div>

          <div className="flex-1 min-w-60">
            <label className="mb-2.5 block text-black font-medium">
              Target Company (will receive data):
            </label>
            <Select
              className="w-full"
              placeholder="Select target company"
              value={targetCompanyId}
              onChange={setTargetCompanyId}
              loading={companiesLoading}
              size="large"
            >
              {companies.map((company) => (
                <Option key={company._id} value={company._id}>
                  {company.companyName}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <Button
              className="bg-primary text-white rounded-sm px-6 py-2 h-12 hover:opacity-90 border-0"
              icon={<SwapOutlined />}
              loading={initiateLoading}
              onClick={handleInitiateMerge}
              size="large"
            >
              Initiate Merge
            </Button>
          </div>
        </div>
      </Card>

      {/* Merge History Section */}
      <Card
        className="rounded-sm border border-stroke bg-white shadow-lg p-3"
        title={
          <div className="border-b border-stroke py-4 px-6.5 flex justify-between items-center">
            <h3 className="font-medium text-black">Merge History</h3>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-black">
                Filter by Status:
              </span>
              <Select
                value={selectedStatus}
                onChange={setSelectedStatus}
                className="w-40"
                size="middle"
              >
                <Option value="pending">Pending</Option>
                <Option value="in_progress">In Progress</Option>
                <Option value="completed">Completed</Option>
                <Option value="failed">Failed</Option>
                <Option value="rolled_back">Rolled Back</Option>
              </Select>
            </div>
          </div>
        }
        headStyle={{ padding: 0, border: "none" }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          scroll={TABLE_SCROLL}
          columns={mergeColumns}
          dataSource={mergesData?.getMergesByStatus || []}
          loading={mergesLoading}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          bordered
          className="border-t-0"
        />
      </Card>

      {/* Merge Details Modal */}
      <Modal
        title={
          <div className="text-lg font-semibold text-black border-b border-stroke pb-3">
            Merge Details
          </div>
        }
        open={mergeDetailsVisible}
        onCancel={() => {
          setMergeDetailsVisible(false);
          setSelectedMergeId(null);
        }}
        footer={null}
        width={900}
        className="rounded-sm"
      >
        {detailsLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading details...</p>
          </div>
        ) : mergeDetailsData?.getMergeDetails ? (
          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded border">
              <h4 className="text-lg font-medium text-black mb-2">
                {
                  mergeDetailsData.getMergeDetails.merge.sourceCompany
                    .companyName
                }{" "}
                →{" "}
                {
                  mergeDetailsData.getMergeDetails.merge.targetCompany
                    .companyName
                }
              </h4>
              <Tag
                color={getStatusColor(
                  mergeDetailsData.getMergeDetails.merge.status
                )}
              >
                {mergeDetailsData.getMergeDetails.merge.status.toUpperCase()}
              </Tag>
            </div>

            {mergeDetailsData.getMergeDetails.merge.logs?.length > 0 && (
              <div className="border border-stroke rounded p-4">
                <h5 className="font-medium text-black mb-3">Process Logs:</h5>
                <Timeline
                  className="mt-4"
                  items={mergeDetailsData.getMergeDetails.merge.logs.map(
                    (log, index) => ({
                      children: (
                        <span className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                          {log}
                        </span>
                      ),
                    })
                  )}
                />
              </div>
            )}

            {mergeDetailsData.getMergeDetails.historyEntries?.length > 0 && (
              <div className="border border-stroke rounded">
                <div className="border-b border-stroke p-4">
                  <h5 className="font-medium text-black">Transfer History</h5>
                </div>
                <Table
                  size="small"
                  columns={[
                    {
                      title: "Type",
                      dataIndex: "recordType",
                      key: "recordType",
                      className: "font-medium",
                    },
                    {
                      title: "Title",
                      dataIndex: ["metadata", "recordTitle"],
                      key: "title",
                    },
                    {
                      title: "Status",
                      dataIndex: "status",
                      key: "status",
                      render: (status) => <Tag color="blue">{status}</Tag>,
                      align: "center",
                    },
                    {
                      title: "Conflict",
                      key: "conflict",
                      align: "center",
                      render: (_, record) =>
                        record.conflictResolution?.hadConflict ? (
                          <Tag color="orange">Resolved</Tag>
                        ) : (
                          <Tag color="green">None</Tag>
                        ),
                    },
                  ]}
                  dataSource={mergeDetailsData.getMergeDetails.historyEntries}
                  rowKey="_id"
                  pagination={false}
                  scroll={{ ...TABLE_SCROLL, y: 300 }}
                  className="border-0"
                />
              </div>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default CompanyMerge;
